<template>
  <FullscreenModal :isActive="showConfirmDelete" @modalCompleted="handleDeleteItemModal">
    <template v-slot:body>
      {{ i18n.t('views.upsertChecklist.deleteMainText') }}
    </template>
    <template v-slot:confirmBtn>
      {{ i18n.t('views.upsertChecklist.deleteConfirmAction') }}
    </template>
    <template v-slot:cancelBtn>
      {{ i18n.t('views.upsertChecklist.deleteCancelAction') }}
    </template>
  </FullscreenModal>

  <FullscreenModal :isActive="showDoubleCheckCancel" @modalCompleted="handleDoubleCheckCancelModal">
    <template v-slot:body>
      {{ i18n.t('actions.doubleCheckCancelMainText') }}
    </template>
    <template v-slot:confirmBtn>
      {{ i18n.t('actions.doubleCheckProceedCancel') }}
    </template>
    <template v-slot:cancelBtn>
      {{ i18n.t('actions.doubleCheckAbandonCancel') }}
    </template>
  </FullscreenModal>

  <div class="container-fluid">
    <div class="row justify-content-center">
      <div class="col col-md-6">
        <h1 class="mt-3" v-show="!showItemForm">{{ title }}</h1>
        <div v-if="!fetchingError">
          <form novalidate class="my-4" @input="autosave" @submit.prevent v-show="!showItemForm">
            <label for="cl-id-input" class="form-label">ID</label>
            <input type="text" id="cl-id-input" class="form-control" :disabled="currentRouteName === 'EditChecklist'"
              v-model="clId" />
            <div class="text-danger" v-html="clIdError"></div>

            <label for="cl-display-name-input" class="form-label mt-3">Nome de exibição</label>
            <input type="text" id="cl-display-name-input" class="form-control" v-model="displayName" />
            <div class="text-danger">{{ displayNameError }}</div>

            <div class="form-check mt-4">
              <input id="cl-nfc-input" type="checkbox" class="form-check-input" v-model="isNfcTagMandatory" />
              <label class="form-check-label" for="cl-nfc-input">
                Uso de tag NFC obrigatório?
              </label>
            </div>

            <div v-if="sortedSectionNames.length > 0">
              <div class="p-3 mt-5 border" v-for="sectionName in sortedSectionNames" :key="sectionName">
                <div class="d-flex justify-content-between">
                  <h3>{{ sectionName }}</h3>
                  <button type="button" class="ms-3 btn btn-outline-success" @click="addItem(sectionName)">
                    <i class="bi bi-plus-circle me-1"></i>
                    Novo item
                  </button>
                </div>
                <div class="mt-3" v-for="item in sections[sectionName]" :key="item.clId">
                  <div class="card mnrt-card mb-2" :class="{ 'shadow-pop-tr': item.isAnimationRunning }">
                    <div class="card-body">
                      <p>{{ item.text }}</p>
                      <div class="float-start">
                        <button class="card-link btn btn-outline-dark" @click="editItem(item.clId)">
                          <i class="bi bi-pencil"></i>
                        </button>
                        <button class="card-link btn btn-outline-danger" @click="deleteItem(item.clId)">
                          <i class="bi bi-x-circle"></i>
                        </button>
                      </div>
                      <div class="float-end">
                        <button class="card-link btn btn-outline-dark" @click="moveItemUp(item)">
                          <i class="bi bi-box-arrow-up"></i>
                        </button>
                        <button class="card-link btn btn-outline-dark" @click="moveItemDown(item)">
                          <i class="bi bi-box-arrow-down"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div v-else class="mt-5 alert alert-info">
              <i class="bi bi-info-square-fill me-2"></i>
              <span>Toda checklist deve ter, ao menos, uma seção e um item.</span>
            </div>

            <hr class="mt-5" />

            <form novalidate class="row row-cols-sm-auto align-items-center g-2 my-5" @submit.prevent>
              <div class="col-12">
                <input class="form-control" type="text" v-model.trim="newSectionName" />
              </div>
              <div class="col-12">
                <button :disabled="newSectionName.length === 0" type="button" class="ms-3 btn btn-outline-success"
                  @click="addSection">
                  <i class="bi bi-plus-circle me-1"></i>
                  Nova seção
                </button>
              </div>
            </form>

            <hr />

            <div class="my-5">
              <button type="button" class="btn btn-success" :disabled="sortedSectionNames.length === 0"
                @click="saveChecklist">
                {{ i18n.t('actions.save') }}
              </button>
              <button type="button" class="ms-3 btn btn-outline-danger" @click="cancelChanges">
                {{ i18n.t('actions.cancel') }}
              </button>
              <div class="mt-3 text-danger">
                {{ checklistError }}
              </div>
            </div>
          </form>

          <ChecklistItemForm v-if="currentItem" :isActive="showItemForm" :checklistSections="sectionsCollection"
            :checklistItemCategories="clItemCategories" :unitsOfMeasurement="unitsOfMeasurement"
            :checklistItemId="currentItemId" :checklistItem="currentItem"
            @checklistItemFormCompleted="checklistItemFormCompleted"
            @checklistItemFormCanceled="checklistItemFormCanceled" />
        </div>
        <p v-else>
          Não foi possível encontrar o checklist especificado ({{
            props.checklistId
          }}).
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import {
  ref,
  reactive,
  computed,
  toValue,
  toRaw,
  toRefs,
  watch,
  onMounted,
  onUnmounted,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import i18n from '@/i18n/i18n';
import store from '@/store/store';
import QaDao from '@/dao/qa_dao';
import checklistDao from '@/dao/checklist_dao';
import FullscreenModal from '@/components/FullscreenModal';
import ChecklistItemForm from '@/components/ChecklistItemForm';
import { validatesPresence } from '@/validators/ui_validators';
import { checklistIdRegex } from '@/validators/regex';
import AppHelper from '@/helpers/app_helper';
import ChecklistHelper from '@/helpers/checklist_helper';
import { useToast } from 'vue-toastification';

export default {
  name: 'ChecklistForm',
  components: {
    FullscreenModal,
    ChecklistItemForm,
  },
  props: {
    checklistId: {
      type: String,
      required: false,
    },
  },
  setup(props) {
    const qaDao = new QaDao();

    const router = useRouter();
    const toast = useToast();

    const model = reactive({
      currentRouteName: '',
      clItemCategories: [{ text: 'Selecione a categoria do item', value: '' }],
      unitsOfMeasurement: [],
      dbCl: null,
      clId: '',
      displayName: '',
      isNfcTagMandatory: true,
      newSectionName: '',
      items: {},
      currentItemId: null,
      currentItem: null,
      showConfirmDelete: false,
      showDoubleCheckCancel: false,
      showItemForm: false,
      clIdError: '',
      displayNameError: '',
      checklistError: '',
      fetchingError: false,
    });

    const title = computed(() => {
      return model.currentRouteName === 'EditChecklist'
        ? 'Editando um checklist'
        : 'Novo checklist';
    });

    const sections = computed(() => {
      return ChecklistHelper.checklistSections(model.items);
    });

    const sortedSectionNames = computed(() => {
      return AppHelper.alphabeticallySortedArrayFromObj(toValue(sections));
    });

    const sectionsCollection = computed(() => {
      return Object.keys(toValue(sections)).map((sectionName) => ({
        text: sectionName,
        value: sectionName,
      }));
    });

    const dbId = () => {
      return model.clId ? `checklist::${model.clId}` : '';
    };

    const addSection = () => {
      // Adding a new section means adding a new item belonging to the section to be created
      model.items[nextItemId()] = newItem({
        type: 'geral',
        section: model.newSectionName,
        text: `Passou nas inspeções de ${model.newSectionName}`,
      });
      model.newSectionName = '';
    };

    const nextItemId = () => {
      if (Object.keys(model.items).length === 0) return '0';

      const sortedIds = Object.keys(model.items).sort((a, b) => a - b);
      return String(Number(sortedIds[sortedIds.length - 1]) + 1);
    };

    const nextItemOrder = (section) => {
      if (!sections.value[section]) return 0; // first item of a section

      const lastSectionItem = sections.value[section][sections.value[section].length - 1];
      return Number(lastSectionItem.order) + 1;
    };

    const newItem = (attrs = {}) => {
      return Object.assign(
        {
          type: '',
          section: '',
          text: '',
          help: '',
          order: nextItemOrder(attrs.section),
          measurement: null,
        },
        attrs
      );
    };

    const addItem = (section) => {
      const item = newItem({
        section: section,
        measurement: null,
      });
      model.currentItemId = null;
      model.currentItem = item;

      showItemForm(true);
    };

    const editItem = (itemId) => {
      model.currentItemId = itemId;
      model.currentItem = model.items[itemId];

      showItemForm(true);
    };

    const deleteItem = (itemId) => {
      model.currentItemId = itemId;
      model.currentItem = model.items[itemId];

      model.showConfirmDelete = true;
    };

    const handleDeleteItemModal = (confirmed) => {
      if (confirmed) {
        const deletedItemSection = model.items[model.currentItemId].section;
        delete model.items[model.currentItemId];

        model.currentItemId = null;
        model.currentItem = null;

        fixSectionItemOrderingGap(deletedItemSection);
      }

      model.showConfirmDelete = false;
    };

    const fixSectionItemOrderingGap = (deletedItemSection) => {
      const section = sections.value[deletedItemSection];
      if (!section)
        return; // the last item of a section just got deleted

      section.forEach((item, idx) => {
        model.items[item.clId].order = idx;
      });
    };

    const moveItemUp = (item) => {
      if (item.order === 0) {
        toast.error(i18n.t('validations.checklistItem.invalidOrder'));
        return;
      }

      const prevItem = sections.value[item.section].find((it) => it.order === item.order - 1);
      prevItem.order = prevItem.order + 1;
      item.order = item.order - 1;
      animateMovingItem(item);
    };

    const moveItemDown = (item) => {
      if (item.order === sections.value[item.section].length - 1) {
        toast.error(i18n.t('validations.checklistItem.invalidOrder'));
        return;
      }

      const nextItem = sections.value[item.section].find((it) => it.order === item.order + 1);
      nextItem.order = nextItem.order - 1;
      item.order = item.order + 1;
      animateMovingItem(item);
    };

    const animateMovingItem = (item) => {
      item.isAnimationRunning = true;
      setTimeout(() => item.isAnimationRunning = false, 250);
    };

    const checklistItemFormCompleted = (checklistItemId, checklistItem) => {
      if (checklistItemId) {
        model.items[checklistItemId] = checklistItem;
      } else {
        // New item
        model.items[nextItemId()] = checklistItem;
      }

      showItemForm(false);
      model.currentItemId = null;
      model.currentItem = null;
    };

    const checklistItemFormCanceled = () => {
      showItemForm(false);
      model.currentItemId = null;
      model.currentItem = null;
    };

    const showItemForm = (isVisible) => {
      window.scroll({ top: 0, left: 0, behavior: 'instant' });
      model.showItemForm = isVisible;
    };

    const validatesId = async () => {
      if (!model.clId) {
        model.clIdError = i18n.t('validations.shared.mustBeFilled');
        return false;
      }

      if (!checklistIdRegex.test(model.clId)) {
        model.clIdError = i18n.t('validations.checklist.id');
        return false;
      }

      // No need to check for a unique ID when editing a checklist
      if (model.currentRouteName !== 'EditChecklist') {
        const uniqueId = await checklistDao.isUniqueId(dbId());
        if (!uniqueId) {
          model.clIdError = i18n.t('validations.shared.mustBeUniqueId');
          return false;
        }
      }

      model.clIdError = '';
      return true;
    };

    const isChecklistValid = async () => {
      validatesPresence(model, 'displayName', 'displayNameError');
      await validatesId(); // TODO Validate against remote DB as well?

      const rawModel = toRaw(model);
      for (const attr in rawModel) {
        // Checking all error messages, returning false if any error is found.
        if (attr.toLowerCase().endsWith('error') && rawModel[attr])
          return false;
      }

      return true;
    };

    const saveChecklist = async () => {
      model.checklistError = '';

      const valid = await isChecklistValid();
      if (!valid) {
        model.checklistError = 'Corrija os campos com erros e tente novamente.';
        return false;
      }

      store.commit('activateFullscreenOverlay');

      const itemsWithRelevantAttrs = {};
      for (const [clId, item] of Object.entries(toValue(model.items))) {
        const itemClone = structuredClone(item);
        delete itemClone.clId;
        itemsWithRelevantAttrs[clId] = itemClone;
      }

      if (model.currentRouteName === 'EditChecklist') {
        const cl = toValue(model.dbCl);
        cl.displayName = toValue(model.displayName);
        cl.items = itemsWithRelevantAttrs;

        await checklistDao.updateChecklist(cl);
      } else {
        const cl = {
          _id: dbId(),
          displayName: toValue(model.displayName),
          items: itemsWithRelevantAttrs,
        };

        await checklistDao.createChecklist(cl);
      }

      const mandatoryNfcTagProducts = await checklistDao.getObjById('list::mandatoryNfcTagProducts');
      const isNfcCurrentlyMandatory = mandatoryNfcTagProducts.values.includes(model.clId);

      if (isNfcCurrentlyMandatory !== model.isNfcTagMandatory) {
        // current status is different than desired; we need to update the list of types of product
        // that always require NFC tags
        if (model.isNfcTagMandatory) {
          mandatoryNfcTagProducts.values.push(model.clId);
        } else {
          mandatoryNfcTagProducts.values = mandatoryNfcTagProducts.values.filter((clId) => clId !== model.clId);
        }

        await qaDao.db().put(mandatoryNfcTagProducts);
      }

      store.commit('deactivateFullscreenOverlay');
      router.push({ name: 'ChecklistList' });
      toast.success(i18n.t('actions.saved'));
    };

    const cancelChanges = () => {
      model.showDoubleCheckCancel = true;
    };

    const handleDoubleCheckCancelModal = (confirmed) => {
      model.showDoubleCheckCancel = false;

      if (confirmed) {
        router.push({ name: 'ChecklistList' });
      }
    };

    onMounted(async () => {
      store.commit('activateFullscreenOverlay');
      model.currentRouteName = useRoute().name;

      const clItemCategories = await checklistDao.getObjById(
        'collection::checklistItemCategories'
      );
      model.clItemCategories.push(...clItemCategories.textAndValues);
      const unitsOfMeasurement = await checklistDao.getObjById(
        'collection::unitsOfMeasurement'
      );
      model.unitsOfMeasurement.push(...unitsOfMeasurement.textAndValues);

      if (
        model.currentRouteName === 'EditChecklist' ||
        model.currentRouteName === 'CloneChecklist'
      ) {
        model.clId = toValue(props.checklistId);
        model.dbCl = await checklistDao.getObjById(dbId());
        if (model.dbCl) {
          model.displayName = model.dbCl.displayName;
          model.items = model.dbCl.items;

          const mandatoryNfcTagProducts = await checklistDao.getObjById('list::mandatoryNfcTagProducts');
          model.isNfcTagMandatory = mandatoryNfcTagProducts.values.includes(model.clId);

          if (model.currentRouteName === 'CloneChecklist') {
            model.clId = '';
          }
        } else {
          model.fetchingError = true;
        }
      }

      store.commit('deactivateFullscreenOverlay');
    });

    return {
      i18n,
      props,
      ...toRefs(model),
      title,
      sections,
      sortedSectionNames,
      sectionsCollection,
      addSection,
      addItem,
      editItem,
      deleteItem,
      handleDeleteItemModal,
      moveItemUp,
      moveItemDown,
      handleDoubleCheckCancelModal,
      checklistItemFormCompleted,
      checklistItemFormCanceled,
      saveChecklist,
      cancelChanges,
    };
  },
};
</script>
